import type { DeadlineSameTypeSummaryDto } from '@prospero/shared';
import { resolveSameTypeRequest } from '../../../../components/deadline-same-type/resolve-same-type-request';
import {
  SameTypeDeadlineNotice,
  type SameTypeLatestAction,
} from '../../../../components/deadline-same-type/same-type-deadline-notice';
import { RoutesList } from '../../../../config/routes-list';
import { useOpenConsultantDeadlineDocument } from '../../../../hooks/consultant/use-consultant-deadline-actions';
import { useConsultantSameTypeSummary } from '../../../../hooks/deadline/use-same-type-summary';
import type { DocumentTypeItem } from '../../../../services/document-type/document-type.service';
import {
  DEFAULT_CONSULTANT_DEADLINES_FILTERS,
  serializeConsultantDeadlinesFilters,
} from '../deadline-url-filters';

interface ConsultantSameTypeNoticeProps {
  tenantIds: readonly string[];
  documentType: DocumentTypeItem | null;
  /** The person chosen for a personal type (single client only). */
  assigneeUserId: string | null | undefined;
  clientNames: Record<string, string>;
  /** The link to the client rows stays in the app: the dialog closes on it. */
  onNavigate: () => void;
}

/** The client's rows of that type in the list (archived view for an archived row): no file to open. */
function buildRowsHref(summary: DeadlineSameTypeSummaryDto, documentTypeId: string): string {
  const filters = serializeConsultantDeadlinesFilters({
    ...DEFAULT_CONSULTANT_DEADLINES_FILTERS,
    tenantId: summary.tenantId,
    documentTypeId,
    archived: summary.latestIsArchived,
  });
  return `${RoutesList.CONSULTANT_DEADLINES}?${filters.toString()}`;
}

function toLatestAction(
  summary: DeadlineSameTypeSummaryDto,
  documentTypeId: string,
  openDocument: (deadlineId: string) => void,
): SameTypeLatestAction {
  return summary.latestHasDocument
    ? { onOpen: () => openDocument(summary.latestDeadlineId) }
    : { href: buildRowsHref(summary, documentTypeId) };
}

export function ConsultantSameTypeNotice({
  tenantIds,
  documentType,
  assigneeUserId,
  clientNames,
  onNavigate,
}: ConsultantSameTypeNoticeProps) {
  const request = resolveSameTypeRequest({
    documentType,
    assigneeUserId,
    clientCount: tenantIds.length,
  });
  const { data: sameTypeSummaries = [] } = useConsultantSameTypeSummary(tenantIds, request);
  const openDocument = useOpenConsultantDeadlineDocument();
  const { documentTypeId } = request;
  if (documentTypeId === null) {
    return null;
  }

  const noticeClients = sameTypeSummaries
    .filter((summary) => tenantIds.includes(summary.tenantId))
    .map((summary) => ({
      tenantId: summary.tenantId,
      clientName: clientNames[summary.tenantId] ?? summary.tenantId,
      activeCount: summary.activeCount,
      archivedCount: summary.archivedCount,
      latestAction: toLatestAction(summary, documentTypeId, (deadlineId) =>
        openDocument.mutate(deadlineId),
      ),
    }));

  return (
    <SameTypeDeadlineNotice
      clients={noticeClients}
      isMultiClient={tenantIds.length > 1}
      onNavigate={onNavigate}
    />
  );
}
